import React from "react";
import { Box, FormHelperText, useMediaQuery, useTheme } from "@mui/material";
import { UseFormRegisterReturn } from "react-hook-form";
import { dt } from "../../../themes/redesignTokens";

export interface FormTextareaProps {
  // react-hook-form mode
  register?: UseFormRegisterReturn;
  error?: string;
  // standalone mode
  value?: string;
  onChange?: (value: string) => void;
  // common
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  minHeight?: string;
  disabled?: boolean;
}

/**
 * Raw <textarea> wrapped in a styled Box.
 *
 * MUST use raw <textarea>, NOT MUI <TextField multiline>.
 * MUI multiline breaks inside dialogs with wildcard font selectors.
 * This is a known production bug. See feedback_mui_textarea_broken.md.
 *
 * Pattern extracted from SR/RFS (identical in both, proven working).
 */
const FormTextarea: React.FC<FormTextareaProps> = ({
  register,
  error,
  value,
  onChange,
  placeholder,
  maxLength,
  rows = 6,
  minHeight = "120px",
  disabled,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const textareaStyle: React.CSSProperties = {
    border: "none",
    outline: "none",
    width: "100%",
    fontSize: "14px",
    fontFamily: "var(--font-sans)",
    color: "#1a1a2e",
    background: "transparent",
    resize: "vertical",
    minHeight,
    boxSizing: "border-box",
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <Box>
      <Box
        sx={{
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          border: error
            ? `1.5px solid #C0392B`
            : "1.5px solid transparent",
          padding: "12px",
          transition: "all 0.2s ease",
          "&:focus-within": {
            border: `1.5px solid ${dt.blue}`,
            boxShadow: `0 0 0 3px ${dt.blueGlow}`,
          },
        }}
      >
        {register ? (
          <textarea
            {...register}
            placeholder={placeholder}
            maxLength={maxLength}
            rows={rows}
            disabled={disabled}
            style={textareaStyle}
          />
        ) : (
          <textarea
            value={value ?? ""}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            rows={rows}
            disabled={disabled}
            style={textareaStyle}
          />
        )}
      </Box>
      {error && (
        <FormHelperText error sx={{ mt: 0.5, fontFamily: dt.font }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default FormTextarea;
